import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'
import { PaymentAccount } from '@klear/payment/payment-account'
import { TraderExternalBankAccount } from '@klear/trader-external-bank-account'
import { Trader } from '@klear/trader'

interface UseFunderPaymentProps {
  sourceId?: number
  destinationId?: number
  disabled?: boolean
}

interface UseFunderPaymentReturn {
  traders: Trader[] | undefined
  isTradersLoading: boolean
  sourcePaymentAccounts: PaymentAccount[]
  destinationPaymentAccounts: PaymentAccount[]
  isSourcePaymentAccountsLoading: boolean
  isDestinationPaymentAccountsLoading: boolean
}

type GetPaymentAccountsOutput = RouterOutputs['payment']['getPaymentAccounts']

function buildPaymentAccounts(data: GetPaymentAccountsOutput): PaymentAccount[] {
  const paymentAccounts: PaymentAccount[] = []
  for (const account of data.klearAccounts ?? []) {
    paymentAccounts.push(PaymentAccount.fromKlearFinancialAccount(account))
  }
  for (const account of data.externalBankAccounts) {
    const paymentAccount =
      account instanceof TraderExternalBankAccount
        ? PaymentAccount.fromTraderExternalBankAccount(account, data.externalBankAccountOrganization)
        : PaymentAccount.fromSupplierExternalBankAccount(account, data.externalBankAccountOrganization)
    paymentAccounts.push(paymentAccount)
  }
  return paymentAccounts
}

export const useFunderPayment = ({
  sourceId,
  destinationId,
  disabled
}: UseFunderPaymentProps): UseFunderPaymentReturn => {
  const { data: traders, isLoading: isTradersLoading } = trpc.trader.list.useQuery(
    {},
    {
      enabled: !disabled
    }
  )

  const { data: sourcePaymentAccounts = [], isLoading: isSourcePaymentAccountsLoading } =
    trpc.payment.getPaymentAccounts.useQuery(
      { organizationId: sourceId || 0 },
      {
        enabled: !!sourceId && !isNaN(sourceId) && !disabled,
        select: (data) => buildPaymentAccounts(data)
      }
    )

  const { data: destinationPaymentAccounts = [], isLoading: isDestinationPaymentAccountsLoading } =
    trpc.payment.getPaymentAccounts.useQuery(
      { organizationId: destinationId || 0 },
      {
        enabled: !!destinationId && !isNaN(destinationId) && !disabled,
        select: (data) => buildPaymentAccounts(data)
      }
    )

  return {
    traders,
    isTradersLoading,
    sourcePaymentAccounts,
    destinationPaymentAccounts,
    isSourcePaymentAccountsLoading,
    isDestinationPaymentAccountsLoading
  }
}
